import { LitElement, html, nothing } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { AreaSeries, ChartType } from '../../gzGeneralChart/types';
import { GzGeneralChartWebComponent } from '../../gzGeneralChart';
import { getEmptyColorsChartColors } from '../../gzGeneralChart/types/mocked.data';

/**
 * @summary Type here the description of this component
 * @tag gz-horizontal-stacked-bar-chart
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-horizontal-stacked-bar-chart')
export class GzHorizontalStackedBarChartWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  chartTitle: string | undefined = undefined;

  @property({ type: String, attribute: true, reflect: true })
  emptyMessage = '';

  @property({
    type: Array,
    attribute: true,
    reflect: false,
  })
  series: AreaSeries[];

  @property({ type: Number, attribute: true, reflect: true })
  height: number;

  @property({ type: Number, attribute: true, reflect: true })
  width: number;

  @property({
    type: Array,
    attribute: true,
    reflect: false,
  })
  categories: string[];

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  emptyState;

  @property({ type: Boolean, attribute: true, reflect: true })
  showTotalDataLabels = false;

  @property({ type: Function }) openNewTabOnLegendClicked: (seriesIndex: number) => void | undefined;

  private seriesMapping = {};

  private defaultColors = ['var(--blue-chart-500)', 'var(--blue-chart-050)', 'var(--purple-chart-050)'];

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  willUpdate(changedProps: Map<string, unknown>) {
    super.willUpdate(changedProps);

    if (changedProps.has('categories') || changedProps.has('series')) {
      this.buildTooltipData();
    }
  }

  render() {
    if (!this.categories || !this.series) {
      return nothing;
    }

    const options = {
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: {
          show: false, // do not show menu with download csv, png, etc
        },
        events: {
          legendClick: (chartContext, seriesIndex, opts) => {
            let sum = 0;
            opts.config.series[seriesIndex].data.forEach((item, index) => {
              sum += item;
            });

            if (sum === 0) {
              return;
            }

            this.openNewTabFromChart(seriesIndex);
          },
          // tslint:disable-next-line:no-shadowed-variable
          dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
            GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
          },
          // tslint:disable-next-line:no-shadowed-variable
          mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
            GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
          },
        },
      },
      colors: this.emptyState ? getEmptyColorsChartColors().lineColor : this.defaultColors,
      fill: {
        opacity: 1,
        colors: this.emptyState ? getEmptyColorsChartColors().fillColor : this.defaultColors,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            total: {
              enabled: this.showTotalDataLabels,
              offsetX: 0,
            },
          },
        },
      },
      stroke: {
        width: 1,
        colors: ['var(--white-layout-301-level-0-elevation)'],
      },
      yaxis: {
        title: {
          text: undefined,
        },
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        intersect: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }): string => {
          return this.seriesMapping[dataPointIndex] ?? series[seriesIndex][dataPointIndex];
        },
      },
      legend: {
        show: true,
        showForNullSeries: true,
        showForZeroSeries: true,
        showForSingleSeries: true,
        position: 'bottom',
        horizontalAlign: 'center',
        formatter: (seriesName, opts) => {
          const allSeriesValues = opts.w.globals.series[opts.seriesIndex];

          if (this.emptyState || allSeriesValues.length === 0) {
            return [`${seriesName}`, ' ', '--'];
          }

          let sum = 0;
          allSeriesValues.forEach(value => (sum += value));

          return [`${seriesName}`, ' ', sum];
        },
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
    };

    return html`<gz-general-chart
      .type="${ChartType.CUSTOM}"
      .series="${this.series}"
      .categories="${this.categories}"
      .customConfig="${options}"
      .chartTitle="${this.chartTitle}"
      .emptyMessage="${this.emptyMessage}"
      .width="${this.width}"
      .height="${this.height}"
    >
    </gz-general-chart>`;
  }

  buildTooltipData() {
    if (!this.categories || !this.series) {
      return;
    }

    for (let dataPointIndexCategory = 0; dataPointIndexCategory < this.categories.length; dataPointIndexCategory++) {
      let seriesDetails = '';

      this.series.forEach((item, index) => {
        if (!item.data[dataPointIndexCategory]) {
          return;
        }

        seriesDetails += `
          <div class="series-container" style="padding-bottom: var(--spacer);">
            <span class='tooltip--bullet' style="background-color: ${this.defaultColors[index]}"></span>
            <span class='tooltip--label' style="color: var(--body-text-arrows-400);">${item.name}: </span>
            <span class='tooltip--value' style="float: right; padding-left: 8px;">${item.data[dataPointIndexCategory]}</span>
          </div>`;
      });

      this.seriesMapping[
        dataPointIndexCategory
      ] = `<div class="tooltip d-flex flex-row" style="align-items: flex-start; flex-direction: column;">
                <span style="color: var(--chart-300-icon-buttons);">${this.categories[dataPointIndexCategory]}</span>
                <div class="tooltip--details" style="padding-top: var(--spacer); width: max-content;">
                  ${seriesDetails}
                </div>
              </div>
          `;
    }
  }

  openNewTabFromChart(seriesIndex) {
    if (!this.openNewTabOnLegendClicked) {
      return;
    }

    this.openNewTabOnLegendClicked(seriesIndex);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-horizontal-stacked-bar-chart': GzHorizontalStackedBarChartWebComponent;
  }
}
